import { 
  BatteryDistribution, 
  Distribution, 
  DistributionManipulationParams, 
  DistributionPreviewParams
} from "../types/percentages";
import { useVirtualBatteries } from "./useVirtualBatteries";

export const useBatteryDistributionPreview = () => {
  const bateriaVirtualTool = useVirtualBatteries();

  const getExpectedDistributionWhenChangedPercentage = ({
    editedBatteryId, contractUpdatedId, newPercentage, forceCurrentName
  }: DistributionManipulationParams<"changePercentage">) => {
    const newBatteryDistribution = bateriaVirtualTool.getBatteryDistribution(
      editedBatteryId
    );

    // INFO: Remove from the distribution the contract being updated to avoid conflict with the new percentage
    newBatteryDistribution.distribution = newBatteryDistribution.distribution.filter((x) => {
      return x.contractId !== contractUpdatedId
    });

    // INFO: Compute the expected distribution including the newPercentage
    const newExpectedDistribution = computeExpectedDistribution(
      newBatteryDistribution,
      newPercentage,
      true
    );

    const contractVirtualBattery = newExpectedDistribution.distribution.find((x) => x.contractName === "current");
    if (contractVirtualBattery) {
      contractVirtualBattery.contractName = forceCurrentName
    }

    return newExpectedDistribution;
  };

  const getExpectedDistributionWhenMovedBattery = ({
    movedBatteryId, newPercentage, forceCurrentName
  }: DistributionManipulationParams<"move">) => {

    // INFO: Get the info for the future "new" battery
    const newBatteryDistribution = bateriaVirtualTool.getBatteryDistribution(
      movedBatteryId
    );

    const newExpectedDistribution = computeExpectedDistribution(
      newBatteryDistribution,
      newPercentage,
      true
    );

    const contractVirtualBattery = newExpectedDistribution["distribution"].find((x) => x.contractName === "current");
    if (contractVirtualBattery) {
      contractVirtualBattery.contractName = forceCurrentName
    }

    // INFO: Return a new object combining the original battery distribution with the updated expected distribution.
    return newExpectedDistribution;
  };


  const getRemovedExepectedDistribution = ({
    targetBatteryId, removedContractId
  }: DistributionManipulationParams<"removed">) => {
    // INFO: Get the info for the future "new" battery
    const newBatteryDistribution = bateriaVirtualTool.getBatteryDistribution(
      targetBatteryId
    );

    // INFO: Remove from the distribution the removed contract
    newBatteryDistribution.distribution = newBatteryDistribution.distribution.filter((x) => {
      return x.contractId !== removedContractId
    });

    const newExpectedDistribution = computeExpectedDistribution(
      newBatteryDistribution,
      0,
      false
    );
    // INFO: Return a new object combining the original battery distribution with the updated expected distribution.
    return newExpectedDistribution;
  };

  const getExpectedDistribution = ({
    expectedAction,  
    ...rest
  }: DistributionPreviewParams) => {
    let batteryDistribution: BatteryDistribution;
    switch ( expectedAction ) {
      case "move":
        batteryDistribution = getExpectedDistributionWhenMovedBattery(rest as DistributionManipulationParams<"move">);
        break;
      case "changePercentage":
        batteryDistribution = getExpectedDistributionWhenChangedPercentage(rest as DistributionManipulationParams<"changePercentage">);
        break;
      case "removed":
        batteryDistribution =  getRemovedExepectedDistribution(rest as DistributionManipulationParams<"removed">);
        break;
      default:
        throw new Error(`The expectedAction: ${expectedAction} provided is not implemented`);
    }

    if (batteryDistribution) {
      batteryDistribution["distribution"] = bateriaVirtualTool.sortDistribution(batteryDistribution["distribution"]);
    }
    return batteryDistribution;
  };

  const computeExpectedDistribution = (
    currentBatteryDistribution: BatteryDistribution, 
    newPercentage: number, 
    includeCurrent: boolean = true
  ) => {
    let newExpectedDistribution: Distribution[] = [];
    // INFO: Compute the future 100% percentage of current battery weights compared to the new percentage.
    let otherBVNewPercentage = 100 - newPercentage;

    // INFO: Calculate the sum of weights from the current battery distribution.
    let otherBVWeightSum = currentBatteryDistribution["distribution"].reduce((acumulator, distribution) => {
      if (!distribution.isBateriaVirtualActive)
        return acumulator
      return distribution["pes"] + acumulator
    }, 0);

    let newWeight = 1;

    // INFO: If the other BV has wheight we can distribute
    if (otherBVWeightSum !== 0) {
      // INFO: Compute the new weight based on the new percentage and the sum of existing weights.
      newWeight = (otherBVWeightSum * newPercentage) / otherBVNewPercentage;

      // INFO: Calculate the total weight including the new weight.
      let newWeightSum = otherBVWeightSum + newWeight;

      // INFO:Iterate through each distribution entry to compute the new expected distribution percentages.
      for (const distribution of currentBatteryDistribution["distribution"]) {
        //let pes = (distribution["pes"] === 0) ? 1 : distribution["pes"]; // If current distribution weight is 0 unlock it setting to 1
        let pes = distribution["pes"]; // If current distribution weight is 0 unlock it setting to 1
        let pesPercentage = 0;
        if (distribution.isBateriaVirtualActive) {
          pesPercentage = (pes / newWeightSum * 100);
        }
        newExpectedDistribution.push({
          contractName: distribution["contractName"],
          pes: distribution["pes"],
          pesPercentage,
          current: false,
          locked: distribution["pes"] === 0,
          isBateriaVirtualActive: distribution["isBateriaVirtualActive"]
        });
      }
    } else {
      // INFO: If the sum of the other BV weights is 0, assume the new weight is 1 to allow for weight distribution with the desired new percentage.
      const otherBVqty = currentBatteryDistribution["distribution"].filter((value, index ,array) => {
        return value.isBateriaVirtualActive;
      }).length;
      otherBVWeightSum = otherBVqty * 1;

      // INFO: Calculate the new weight using the formula: (newPercentage * quantity of other BV) / (remaining percentage)
      newWeight = (otherBVqty*newPercentage) / otherBVNewPercentage;

      let newWeightSum = otherBVWeightSum + newWeight;

      // INFO: Iterate through each distribution entry to calculate the new expected distribution percentages.
      for (const distribution of currentBatteryDistribution["distribution"]) {
        let pesPercentage = 0;
        if (distribution.isBateriaVirtualActive) {
          pesPercentage = (1 / newWeightSum * 100)
        }
        newExpectedDistribution.push({
          contractName: distribution["contractName"],
          pes: distribution["pes"],
          pesPercentage,
          current: false,
          locked: false,
          isBateriaVirtualActive: distribution["isBateriaVirtualActive"]
        });
      }
    }
    // INFO: Add an entry for the new weight with its percentage to the expected distribution.
    if (includeCurrent) {
        newExpectedDistribution.push({
          contractName: "current",
          pes: newWeight,
          pesPercentage: newPercentage,
          current: true,
          locked: false,
        });
    }

    // INFO: Return a new object combining the original battery distribution with the updated expected distribution.
    return {
      ...currentBatteryDistribution,
      distribution: newExpectedDistribution,
    } as BatteryDistribution;
  };

  return {
    getExpectedDistribution
  };
};

export default useBatteryDistributionPreview;
